import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useColorMode, type ColorMode } from "@chakra-ui/react";
import posthog from "posthog-js";
import type { Icons } from "components/icon/iconTypes";
import {
  ActionCommand,
  CommandCollection,
} from "components/commandPalette/Command";
import {
  DARK_MODE_MEDIA_QUERY,
  getStoredPreference,
  getSystemColorMode,
  persistPreference,
  resolveColorMode,
  toThemeAnalyticsProperties,
  type ThemePreference,
} from "./themePreference";

export type { ThemePreference } from "./themePreference";

export const THEME_OPTIONS: Array<{
  value: ThemePreference;
  title: string;
  description: string;
  icon: Icons;
}> = [
  {
    value: "light",
    title: "Light",
    description: "Keep Tiger Console in light mode",
    icon: "elements/Sun",
  },
  {
    value: "dark",
    title: "Dark",
    description: "Keep Tiger Console in dark mode",
    icon: "elements/Moon",
  },
  {
    value: "system",
    title: "System",
    description: "Match your operating system's appearance setting",
    icon: "elements/Monitor",
  },
] as const;

type ThemePreferenceContextValue = {
  preference: ThemePreference;
  effectiveColorMode: ColorMode;
  setPreference: (nextPreference: ThemePreference) => void;
};

const ThemePreferenceContext =
  createContext<ThemePreferenceContextValue | null>(null);

export const ThemePreferenceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { setColorMode } = useColorMode();
  const [preference, setPreferenceState] =
    useState<ThemePreference>(getStoredPreference);
  const [systemColorMode, setSystemColorMode] =
    useState<ColorMode>(getSystemColorMode);

  const effectiveColorMode = resolveColorMode(preference, systemColorMode);

  useEffect(() => {
    if (preference !== "system" || typeof window === "undefined") {
      return;
    }

    const mediaQueryList = window.matchMedia(DARK_MODE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemColorMode(event.matches ? "dark" : "light");
    };

    setSystemColorMode(mediaQueryList.matches ? "dark" : "light");

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange);
      return () => mediaQueryList.removeEventListener("change", handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [preference]);

  useLayoutEffect(() => {
    setColorMode(effectiveColorMode);

    const root = document.documentElement;
    if (effectiveColorMode === "dark") {
      root.classList.add("dark", "theme__dark");
      root.classList.remove("light", "theme__light");
    } else {
      root.classList.add("light", "theme__light");
      root.classList.remove("dark", "theme__dark");
    }
  }, [effectiveColorMode, setColorMode]);

  // Super properties: every event records the theme the user was seeing,
  // including users who never touch the theme menu.
  useEffect(() => {
    posthog.register(
      toThemeAnalyticsProperties(preference, effectiveColorMode),
    );
  }, [preference, effectiveColorMode]);

  const setPreference = useCallback(
    (nextPreference: ThemePreference) => {
      if (nextPreference === preference) {
        return;
      }

      // `$set` updates the person profile right away. PostHog ignores it for
      // anonymous visitors, so it never creates a profile for logged-out
      // traffic. `posthog.identify` sets the same properties on each app load.
      posthog.capture(
        "switched_theme",
        { from: preference, to: nextPreference },
        {
          $set: toThemeAnalyticsProperties(
            nextPreference,
            // Read the OS setting fresh: `systemColorMode` only tracks it
            // while the preference is already "system".
            resolveColorMode(nextPreference, getSystemColorMode()),
          ),
        },
      );

      setPreferenceState(nextPreference);
      persistPreference(nextPreference);
    },
    [preference],
  );

  const value = useMemo(
    () => ({
      preference,
      effectiveColorMode,
      setPreference,
    }),
    [effectiveColorMode, preference, setPreference],
  );

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  );
};

export const useThemePreference = () => {
  const context = useContext(ThemePreferenceContext);

  if (!context) {
    throw new Error(
      "useThemePreference must be used within ThemePreferenceProvider",
    );
  }

  return context;
};

export const ThemePreferenceCommands = () => {
  const { preference, setPreference } = useThemePreference();

  return (
    <CommandCollection
      id="change_theme"
      group="actions"
      icon="elements/Sun"
      title="Change theme"
      description="Choose a light, dark, or system-based appearance"
      keywords={["theme", "dark", "light", "system", "mode", "appearance"]}
    >
      {THEME_OPTIONS.map((opt) => (
        <ActionCommand
          key={opt.value}
          id={`change_theme_${opt.value}`}
          group="actions"
          icon={opt.icon}
          title={opt.title}
          description={opt.description}
          keywords={["theme", opt.value, opt.title.toLowerCase()]}
          disabled={opt.value === preference}
          onRun={() => setPreference(opt.value)}
        />
      ))}
    </CommandCollection>
  );
};
